import React, { useEffect, useState } from "react";
import useStore from "../useStore";
import * as THREE from "three";
import { BufferGeometryUtils } from "three/examples/jsm/utils/BufferGeometryUtils.js";

const createMesh = (positions, dimensions, rgbColor) => {
  const geometries = [];

  for (let index = 0; index < positions.length; index++) {
    const { x, y, z } = positions[index];

    const geometry = new THREE.BoxGeometry(
      dimensions.x,
      dimensions.y,
      dimensions.z
    );
    geometry.applyMatrix4(new THREE.Matrix4().makeTranslation(x, y, z));

    const rgb = rgbColor;
    const numVerts = geometry.getAttribute("position").count;
    const itemSize = 3; // r, g, b
    const colorsArray = new Uint8Array(itemSize * numVerts);

    // copy the color into the colors array for each vertex
    colorsArray.forEach((v, ndx) => {
      colorsArray[ndx] = rgb[ndx % 3];
    });

    const colorAttrib = new THREE.BufferAttribute(colorsArray, 3, true);

    geometry.setAttribute("color", colorAttrib);

    geometries.push(geometry);
  }

  const mergedFullGeometry = BufferGeometryUtils.mergeBufferGeometries(
    geometries,
    false
  );

  const mesh = (
    <mesh receiveShadow geometry={mergedFullGeometry}>
      <meshStandardMaterial attach="material" vertexColors={true} />
    </mesh>
  );

  return mesh;
};

const berriesGroupCoords = [13, 20, 6];
// prettier-ignore
const berriesPositions = [
  { x: 2.3, y: 10.7, z: 6 },
  { x: 1.3, y: 9.3, z: 6.3 },
  { x: 0.3, y: 11.3, z: 6.3 },
  { x: 3.3, y: 7.3, z: 4.3 },
  { x: 0.3, y: 4.3, z: 6.3 },
  { x: 2.3, y: 5.6, z: 6.3 },
];
const berriesDimensions = { x: 0.3, y: 0.3, z: 0.3 };

const screenCodingGroupCoords = [3, 18, 21.51];
// prettier-ignore
const screenCodingPositions = [
  { x: 0, y: 0, z: 0 }, { x: 1, y: 0, z: 0 },
  { x: 0, y: -1, z: 0 }, { x: 1, y: -1, z: 0 }, { x: 2, y: -1, z: 0 }, { x: 4, y: -1, z: 0 }, { x: 6, y: -1, z: 0 }, { x: 7, y: -1, z: 0 }, { x: 9, y: -1, z: 0 },
  { x: 0, y: -2, z: 0 }, { x: 2, y: -2, z: 0 }, { x: 3, y: -2, z: 0 }, { x: 4, y: -2, z: 0 }, { x: 5, y: -2, z: 0 }, { x: 7, y: -2, z: 0 }, { x: 8, y: -2, z: 0 },
  { x: 0, y: -3, z: 0 }, { x: 1, y: -3, z: 0 }, { x: 3, y: -3, z: 0 }, { x: 4, y: -3, z: 0 }, { x: 6, y: -3, z: 0 }, { x: 7, y: -3, z: 0 }, { x: 8, y: -3, z: 0 }, { x: 9, y: -3, z: 0 },
  { x: 0, y: -4, z: 0 }, { x: 2, y: -4, z: 0 }, { x: 3, y: -4, z: 0 }, { x: 4, y: -4, z: 0 }, { x: 5, y: -4, z: 0 }, { x: 6, y: -4, z: 0 }, { x: 8, y: -4, z: 0 },
];
const screenCodingDimensions = { x: 1, y: 0.3, z: 0.3 };

const screenDesignGroupCoords = [3, 17, 21.51];
// prettier-ignore
const screenDesignPositions = [
  {x: 0, y: 0, z: 0 }, {x: 0.3, y: 0, z: 0 }, {x: 0.6, y: 0, z: 0 }, {x: 0.9, y: 0, z: 0 }, {x: 1.2, y: 0, z: 0 }, 
  {x: 0, y: 0.3, z: 0 }, {x: 1.2, y: 0.3, z: 0 }, 
  {x: 0, y: 0.6, z: 0 }, {x: 1.2, y: 0.6, z: 0 }, 
  {x: 0, y: 0.9, z: 0 }, {x: 1.2, y: 0.9, z: 0 }, 
  {x: 0, y: 1.2, z: 0 }, {x: 0.3, y: 1.2, z: 0 }, {x: 0.6, y: 1.2, z: 0 }, {x: 0.9, y: 1.2, z: 0 }, {x: 1.2, y: 1.2, z: 0 }, 
  
  {x: 2, y: 0, z: 0 }, {x: 2.3, y: 0, z: 0 }, {x: 2.6, y: 0, z: 0 }, {x: 2.9, y: 0, z: 0 }, {x: 3.2, y: 0, z: 0 }, 

  {x: 4, y: 0, z: 0 }, {x: 4.3, y: 0, z: 0 }, {x: 4.6, y: 0, z: 0 }, {x: 4.9, y: 0, z: 0 }, {x: 5.2, y: 0, z: 0 },  

  {x: 0, y: -0.7, z: 0 }, {x: 0.3, y: -0.7, z: 0 }, {x: 0.6, y: -0.7, z: 0 }, {x: 0.9, y: -0.7, z: 0 }, {x: 1.2, y: -0.7, z: 0 }, {x: 1.5, y: -0.7, z: 0 }, {x: 1.8, y: -0.7, z: 0 }, {x: 2.1, y: -0.7, z: 0 }, {x: 2.4, y: -0.7, z: 0 }, {x: 2.7, y: -0.7, z: 0 }, 
  {x: 3, y: -0.7, z: 0 }, {x: 3.3, y: -0.7, z: 0 }, {x: 3.6, y: -0.7, z: 0 }, {x: 3.9, y: -0.7, z: 0 }, {x: 4.2, y: -0.7, z: 0 }, {x: 4.5, y: -0.7, z: 0 }, {x: 4.8, y: -0.7, z: 0 }, {x: 5.1, y: -0.7, z: 0 }, {x: 5.4, y: -0.7, z: 0 },
  {x: 0, y: -1, z: 0 },{x: 5.4, y: -1, z: 0 },
  {x: 0, y: -1.3, z: 0 },{x: 5.4, y: -1.3, z: 0 },
  {x: 0, y: -1.6, z: 0 },{x: 5.4, y: -1.6, z: 0 },
  {x: 0, y: -1.9, z: 0 },{x: 5.4, y: -1.9, z: 0 },
  {x: 0, y: -2.2, z: 0 },{x: 5.4, y: -2.2, z: 0 },
  {x: 0, y: -2.5, z: 0 },{x: 5.4, y: -2.5, z: 0 },
  {x: 0, y: -2.8, z: 0 },{x: 5.4, y: -2.8, z: 0 },
  {x: 0, y: -3.1, z: 0 }, {x: 0.3, y: -3.1, z: 0 }, {x: 0.6, y: -3.1, z: 0 }, {x: 0.9, y: -3.1, z: 0 }, {x: 1.2, y: -3.1, z: 0 }, {x: 1.5, y: -3.1, z: 0 }, {x: 1.8, y: -3.1, z: 0 }, {x: 2.1, y: -3.1, z: 0 }, {x: 2.4, y: -3.1, z: 0 }, {x: 2.7, y: -3.1, z: 0 }, 
  {x: 3, y: -3.1, z: 0 }, {x: 3.3, y: -3.1, z: 0 }, {x: 3.6, y: -3.1, z: 0 }, {x: 3.9, y: -3.1, z: 0 }, {x: 4.2, y: -3.1, z: 0 }, {x: 4.5, y: -3.1, z: 0 }, {x: 4.8, y: -3.1, z: 0 }, {x: 5.1, y: -3.1, z: 0 }, {x: 5.4, y: -3.1, z: 0 },
  
  {x: 6.2, y: 1.2, z: 0 }, {x: 6.5, y: 1.2, z: 0 }, {x: 6.8, y: 1.2, z: 0 }, {x: 7.1, y: 1.2, z: 0 }, 
  {x: 6.2, y: 0.6, z: 0 }, {x: 6.5, y: 0.6, z: 0 }, {x: 6.8, y: 0.6, z: 0 }, {x: 7.1, y: 0.6, z: 0 }, {x: 7.4, y: 0.6, z: 0 }, {x: 7.7, y: 0.6, z: 0 },
  {x: 6.2, y: 0, z: 0 }, {x: 6.5, y: 0, z: 0 }, {x: 6.8, y: 0, z: 0 }, {x: 7.1, y: 0, z: 0 }, {x: 7.4, y: 0, z: 0 }, 
  
  {x: 6.2, y: -0.7, z: 0 }, {x: 6.5, y: -0.7, z: 0 }, {x: 6.8, y: -0.7, z: 0 },
  {x: 6.2, y: -1.3, z: 0 }, {x: 6.5, y: -1.3, z: 0 }, {x: 6.8, y: -1.3, z: 0 }, {x: 7.1, y: -1.3, z: 0 }, {x: 7.4, y: -1.3, z: 0 }, 
  {x: 6.2, y: -1.9, z: 0 }, {x: 6.5, y: -1.9, z: 0 }, {x: 6.8, y: -1.9, z: 0 }, {x: 7.1, y: -1.9, z: 0 }, {x: 7.4, y: -1.9, z: 0 }, {x: 7.7, y: -1.9, z: 0 },
  {x: 6.2, y: -2.5, z: 0 }, {x: 6.5, y: -2.5, z: 0 }, {x: 6.8, y: -2.5, z: 0 }, {x: 7.1, y: -2.5, z: 0 },
  {x: 6.2, y: -3.1, z: 0 }, {x: 6.5, y: -3.1, z: 0 }, {x: 6.8, y: -3.1, z: 0 }, {x: 7.1, y: -3.1, z: 0 }, {x: 7.4, y: -3.1, z: 0 }, {x: 7.7, y: -3.1, z: 0 },
];
const screenDesignDimensions = { x: 0.3, y: 0.3, z: 0.3 };

const Details = ({ ...rest }) => {
  const [currentProject, setCurrentProject] = useState(null);
  const [currentSkill, setCurrentSkill] = useState(null);

  useEffect(() => {
    const unsubscribeProjects = useStore.subscribe(
      (activeProject) => {
        setCurrentProject(activeProject);
      },
      (state) => state.activeProject
    );
    const unsubscribeSkills = useStore.subscribe(
      (activeSkill) => {
        setCurrentSkill(activeSkill);
      },
      (state) => state.activeSkill
    );
    return () => {
      unsubscribeProjects();
      unsubscribeSkills();
    };
  }, []);

  return (
    <group {...rest}>
      <group position={berriesGroupCoords} visible={currentProject === 0}>
        {createMesh(berriesPositions, berriesDimensions, [100, 78, 209])}
      </group>
      <group position={berriesGroupCoords} visible={currentProject === 1}>
        {createMesh(berriesPositions, berriesDimensions, [224, 224, 224])}
      </group>
      <group position={screenCodingGroupCoords} visible={currentSkill === 0}>
        {createMesh(screenCodingPositions, screenCodingDimensions, [0, 255, 0])}
      </group>
      <group position={screenDesignGroupCoords} visible={currentSkill === 1}>
        {createMesh(
          screenDesignPositions,
          screenDesignDimensions,
          [255, 255, 255]
        )}
      </group>
    </group>
  );
};

export default Details;
